import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { CAlert, CButton, CCard, CCardBody, CContainer, CForm, CFormInput, CFormLabel, CSpinner } from '@coreui/react';
import { api, problemMessage, unauthorized, type Schemas } from '../api/client';
import { initTokenSource, isEmbedded, setStandaloneToken } from './tokenSource';

type Me = Schemas['MeResponse'];

const MeContext = createContext<{ me: Me; setMe: (me: Me) => void } | null>(null);

/** Přihlášený člověk. Uvnitř `AuthGate` je vždy k dispozici. */
export function useMe(): Me {
  return useMeContext().me;
}

/** Po změně vlastního profilu (třeba nálepek), ať se to projeví hned, bez obnovení stránky. */
export function useSetMe(): (me: Me) => void {
  return useMeContext().setMe;
}

function useMeContext() {
  const context = useContext(MeContext);
  if (!context) {
    throw new Error('useMe() mimo AuthGate.');
  }

  return context;
}

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; me: Me }
  | { kind: 'needs-login'; message?: string }
  | { kind: 'error'; message: string };

/**
 * Pustí dál, až když víme, kdo je přihlášený. Uvnitř portálu se token získá sám;
 * samostatně se přihlašuje portálovým e-mailem a heslem.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ kind: 'loading' });

  const signIn = useCallback(async () => {
    setState({ kind: 'loading' });

    let result;
    try {
      result = await api.GET('/api/v1/me');
    } catch (error) {
      // Typicky portal neodpovedel na zadost o token. Bez tohohle by aplikace
      // zustala navzdy na tocicim se kolecku.
      setState({ kind: 'error', message: error instanceof Error ? error.message : 'Přihlášení se nezdařilo.' });
      return;
    }

    const { data, response } = result;

    if (data) {
      setState({ kind: 'ready', me: data });
      return;
    }

    if (response.status === 403) {
      // Autentizovany, ale bez pristupu - do aplikace smi jen portalovi GlobalAdmini.
      setState({ kind: 'error', message: 'Do Zakázek mají přístup jen globální administrátoři portálu.' });
    } else if (response.status === 503) {
      setState({ kind: 'error', message: 'Portál je teď nedostupný. Zkuste to prosím za chvíli.' });
    } else if (isEmbedded) {
      setState({ kind: 'error', message: 'Nepodařilo se přihlásit přes portál. Zkuste stránku obnovit.' });
    } else {
      setState({ kind: 'needs-login' });
    }
  }, []);

  useEffect(() => {
    initTokenSource()
      .then(signIn)
      .catch((error: Error) => setState({ kind: 'error', message: error.message }));
  }, [signIn]);

  useEffect(() => {
    const onUnauthorized = () => setState(isEmbedded
      ? { kind: 'error', message: 'Přihlášení vypršelo. Obnovte prosím stránku.' }
      : { kind: 'needs-login', message: 'Přihlášení vypršelo, přihlaste se prosím znovu.' });

    unauthorized.addEventListener('unauthorized', onUnauthorized);
    return () => unauthorized.removeEventListener('unauthorized', onUnauthorized);
  }, []);

  switch (state.kind) {
    case 'loading':
      return (
        <div className="d-flex vh-100 justify-content-center align-items-center">
          <CSpinner color="primary" />
        </div>
      );

    case 'error':
      return (
        <CContainer className="py-5" sm>
          <CAlert color="danger">{state.message}</CAlert>
        </CContainer>
      );

    case 'needs-login':
      return <LoginForm message={state.message} onToken={(token) => { setStandaloneToken(token); void signIn(); }} />;

    case 'ready':
      return (
        <MeContext.Provider value={{ me: state.me, setMe: (me) => setState({ kind: 'ready', me }) }}>
          {children}
        </MeContext.Provider>
      );
  }
}

/**
 * Přihlášení mimo portál. Uvnitř portálu se nikdy neukáže — token tam přichází od portálu.
 * Stejné údaje jako do portálu; heslo jde přes naši službu na portál a nikde se neukládá.
 */
function LoginForm({ message, onToken }: { message?: string; onToken: (token: string) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);

    const { data, error: problem } = await api.POST('/api/v1/auth/login', { body: { email, password } });

    setBusy(false);

    if (data) {
      onToken(data.token);
    } else {
      setError(problemMessage(problem));
    }
  }

  return (
    <CContainer className="py-5" style={{ maxWidth: 420 }}>
      <CCard>
        <CCardBody className="p-4">
          <h4 className="mb-1">Zakázky</h4>
          <p className="text-body-secondary mb-4">Přihlaste se stejně jako do portálu.</p>

          {message && !error && <CAlert color="warning">{message}</CAlert>}
          {error && <CAlert color="danger">{error}</CAlert>}

          <CForm onSubmit={(e) => { e.preventDefault(); void submit(); }}>
            <CFormLabel htmlFor="email">E-mail</CFormLabel>
            <CFormInput
              id="email" type="email" autoComplete="username" autoFocus required
              value={email} onChange={(e) => setEmail(e.target.value)} className="mb-3"
            />
            <CFormLabel htmlFor="password">Heslo</CFormLabel>
            <CFormInput
              id="password" type="password" autoComplete="current-password" required
              value={password} onChange={(e) => setPassword(e.target.value)} className="mb-4"
            />
            <CButton type="submit" color="primary" className="w-100" disabled={busy}>
              {busy ? <CSpinner size="sm" /> : 'Přihlásit'}
            </CButton>
          </CForm>
        </CCardBody>
      </CCard>
    </CContainer>
  );
}
